import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import AppFooter from "@/components/AppFooter";
import Logo from "@/components/Logo";
import { ArrowLeft, ShieldCheck, Lock, Server, Eye, Users, FileKey } from "lucide-react";

const PRINCIPLE_ICONS = [ShieldCheck, Lock, Server, Eye, Users, FileKey];

export default function SicherheitPage() {
  const { t } = useI18n();
  const page = t.legal.sicherheit;

  return (
    <div className="min-h-screen bg-ag-paper ag-paper-grain" data-testid="page-sicherheit">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-12 pb-8 ag-fade-in">
        <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-ag-ink mb-8">
          <ArrowLeft className="h-3.5 w-3.5" /> {t.common.backHome}
        </Link>

        <header className="mb-12 max-w-2xl">
          <Logo variant="full" tone="light" className="h-14 w-auto mb-6 -ml-1" />
          <h1 className="font-serif text-4xl md:text-5xl text-ag-ink mb-4 leading-tight">{page.title}</h1>
          <p className="text-slate-600 leading-relaxed text-[15px]">{page.intro}</p>
        </header>

        {/* Promise card */}
        <div className="ag-card p-7 mb-12 border-emerald-200/60 bg-gradient-to-br from-emerald-50/40 to-white">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-emerald-700" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-serif text-xl text-ag-ink mb-1">{page.promiseTitle}</div>
              <p className="text-slate-700 leading-relaxed text-[15px]">{page.promiseBody}</p>
            </div>
          </div>
        </div>

        {/* Principles grid */}
        <div className="grid md:grid-cols-2 gap-5 mb-12">
          {page.principles.map((p, i) => {
            const Icon = PRINCIPLE_ICONS[i % PRINCIPLE_ICONS.length];
            return (
              <div key={i} data-testid={`sec-principle-${i}`} className="ag-card p-6 ag-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-4">
                  <Icon className="h-4 w-4 text-slate-700" strokeWidth={1.5} />
                </div>
                <div className="font-serif text-lg text-ag-ink mb-2 leading-snug">{p.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>

        {/* Detail sections */}
        <article className="space-y-10">
          {page.sections.map((sec, i) => (
            <section key={i} className="ag-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
              <h2 className="font-serif text-2xl text-ag-ink mb-3">{sec.title}</h2>
              <p className="text-[15px] text-slate-700 leading-relaxed">{sec.body}</p>
            </section>
          ))}
        </article>

        {/* Closing */}
        <div className="ag-card p-7 mt-12 text-center max-w-xl mx-auto">
          <p className="font-serif text-xl text-ag-ink leading-snug">{page.closing}</p>
        </div>

        <AppFooter />
      </div>
    </div>
  );
}
